import { Bell, Loader2, AlertCircle } from 'lucide-react';
import { ScrollArea } from '@/components/ui/scroll-area';
import { NotificationItem } from './NotificationItem';
import type { Notification } from '@/types';

interface NotificationListProps {
  notifications: Notification[];
  isLoading?: boolean;
  error?: Error | null;
  compact?: boolean;
  showDelete?: boolean;
  maxHeight?: string;
  onItemClose?: () => void;
}

export function NotificationList({
  notifications,
  isLoading,
  error,
  compact = false,
  showDelete = false,
  maxHeight,
  onItemClose,
}: NotificationListProps) {
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-8 text-center">
        <AlertCircle className="h-8 w-8 text-destructive/60 mb-2" />
        <p className="text-sm text-muted-foreground">Failed to load notifications</p>
      </div>
    );
  }

  if (notifications.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-8 text-center">
        <Bell className="h-8 w-8 text-muted-foreground/50 mb-2" />
        <p className="text-sm font-medium">No notifications</p>
        <p className="text-xs text-muted-foreground mt-1">You're all caught up!</p>
      </div>
    );
  }

  const content = (
    <div className="divide-y">
      {notifications.map((notification) => (
        <NotificationItem
          key={notification.id}
          notification={notification}
          compact={compact}
          showDelete={showDelete}
          onClose={onItemClose}
        />
      ))}
    </div>
  );

  if (maxHeight) {
    return <ScrollArea className={maxHeight}>{content}</ScrollArea>;
  }

  return content;
}
